"use client";

import React, { useState, useEffect, Suspense } from "react";
import { useSearchParams } from "next/navigation";

interface StatItem {
  id: string;
  claim: string;
  dataPoint: string;
  sourceName: string;
  sourceUrl: string;
  citationText: string;
  htmlEmbed: string;
}

const REGIONAL_INDEX = [
  { region: "New England", states: "ME, NH, VT, MA, RI, CT", multiplier: "1.18x", variance: "+18% above national average" },
  { region: "Middle Atlantic", states: "NY, NJ, PA", multiplier: "1.15x", variance: "+15% above national average" },
  { region: "Pacific Coast", states: "WA, OR, CA", multiplier: "1.22x", variance: "+22% above national average" },
  { region: "Mountain Division", states: "MT, ID, WY, NV, UT, CO, AZ, NM", multiplier: "1.02x", variance: "+2% above national average" },
  { region: "East North Central", states: "MI, OH, IN, IL, WI", multiplier: "0.98x", variance: "-2% below national average" },
  { region: "South Atlantic", states: "DE, MD, VA, WV, NC, SC, GA, FL", multiplier: "0.94x", variance: "-6% below national average" },
  { region: "West South Central", states: "TX, OK, AR, LA", multiplier: "0.88x", variance: "-12% below national average" },
  { region: "East South Central", states: "KY, TN, MS, AL", multiplier: "0.83x", variance: "-17% below national average" },
];

const INFLATION_TRACKER = [
  { category: "Asphalt Shingle Roofing", unit: "per Square (100 Sq Ft)", cost2024: "$450", cost2026: "$580", increase: "+28.8%" },
  { category: "Vinyl Siding Installation", unit: "per Square Foot", cost2024: "$4.80", cost2026: "$6.20", increase: "+29.1%" },
  { category: "Fiber-Cement Siding", unit: "per Square Foot", cost2024: "$9.50", cost2026: "$11.80", increase: "+24.2%" },
  { category: "Composite Decking Boards", unit: "per Square Foot", cost2024: "$7.20", cost2026: "$9.10", increase: "+26.3%" },
  { category: "Residential Concrete Pour", unit: "per Cubic Yard", cost2024: "$135", cost2026: "$168", increase: "+24.4%" },
];

const DIY_FAILURES = [
  { project: "Shower Tile Installation", error: "Missing or punctured waterproofing membrane (leaks to subfloor)", initialProCost: "$1,800", remediationCost: "$4,500", penalty: "2.5x Cost (requires gutting & rot repair)" },
  { project: "Window Replacement", error: "Sill flashing installed backwards (water drains into house wall)", initialProCost: "$450", remediationCost: "$2,200", penalty: "4.8x Cost (requires siding removal & studs drying)" },
  { project: "Deck Joist Connection", error: "Ledger attached with nails/screws instead of structural thru-bolts", initialProCost: "$900", remediationCost: "$2,900", penalty: "3.2x Cost (requires structural retrofitting)" },
  { project: "Roof Shingle Overlap", error: "Incorrect nail placement and inadequate valley water barrier", initialProCost: "$3,200", remediationCost: "$7,800", penalty: "2.4x Cost (requires structural plywood replacement)" },
];

const ENERGY_PAYBACK = [
  { upgrade: "Air Sealing & Weatherstripping", avgUpfrontCost: "$450", energyBillSavings: "15%", paybackPeriod: "1.5 - 3 Years" },
  { upgrade: "Attic Insulation Upgrade (R-19 to R-60)", avgUpfrontCost: "$1,800", energyBillSavings: "18%", paybackPeriod: "3 - 5 Years" },
  { upgrade: "Heat Pump HVAC System Replacement", avgUpfrontCost: "$7,500", energyBillSavings: "30%", paybackPeriod: "7 - 9 Years" },
  { upgrade: "Double-Pane Low-E Window Replacement", avgUpfrontCost: "$12,000", energyBillSavings: "9%", paybackPeriod: "12 - 20 Years" },
];

const STATS_DATA: Record<string, StatItem[]> = {
  roi: [
    {
      id: "roi-siding",
      claim: "Exterior siding replacement yields the highest resale return of any major home renovation, returning an average of 78.2% of invested costs.",
      dataPoint: "78.2% ROI",
      sourceName: "Meazora Remodeling ROI Report 2026",
      sourceUrl: "https://www.meazora.com/articles/siding-replacement-cost-guide/",
      citationText: "Meazora. (2026). Siding Replacement Resale Return on Investment. Retrieved from Meazora Industry Reports.",
      htmlEmbed: 'Source: <a href="https://www.meazora.com/articles/siding-replacement-cost-guide/">Meazora Siding Cost & ROI Guide</a>'
    },
    {
      id: "roi-roofing",
      claim: "Standard asphalt shingle roof replacement recovers an average of 65.5% of construction costs at home resale.",
      dataPoint: "65.5% ROI",
      sourceName: "Meazora Home Value Survey 2026",
      sourceUrl: "https://www.meazora.com/articles/roof-lifespan-by-material/",
      citationText: "Meazora. (2026). Roof Replacement Valuation & Resale Trends. Retrieved from Meazora.",
      htmlEmbed: 'Source: <a href="https://www.meazora.com/articles/roof-lifespan-by-material/">Meazora Roof Replacement Guide</a>'
    },
    {
      id: "roi-kitchen",
      claim: "Minor cosmetic kitchen remodels (refacing and countertops) yield a 72.1% resale return, significantly higher than luxury major kitchen remodels (53.5%).",
      dataPoint: "72.1% vs 53.5% ROI",
      sourceName: "NKBA & Meazora Annual Cost Study",
      sourceUrl: "https://www.meazora.com/articles/kitchen-remodel-cost-guide/",
      citationText: "National Kitchen and Bath Association & Meazora. (2026). Residential Kitchen Remodeling Cost vs Value. Joint Report.",
      htmlEmbed: 'Source: <a href="https://www.meazora.com/articles/kitchen-remodel-cost-guide/">Meazora Kitchen Remodel Guide</a>'
    }
  ]
};

function StatisticsClientContent() {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const embedMode = searchParams ? searchParams.get("embed") : null;
  const isEmbedParam = embedMode !== null && embedMode !== "false";
  const [isEmbedded, setIsEmbedded] = useState(isEmbedParam);

  // States for copy-paste embed code widgets (visible only in full hub view)
  const [showEmbed1, setShowEmbed1] = useState(false);
  const [showEmbed2, setShowEmbed2] = useState(false);
  const [showEmbed3, setShowEmbed3] = useState(false);
  const [showEmbed4, setShowEmbed4] = useState(false);
  const [showEmbed5, setShowEmbed5] = useState(false);
  const [showEmbed6, setShowEmbed6] = useState(false);
  const [activeRoiEmbedId, setActiveRoiEmbedId] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      let isInsideIframe = false;
      try {
        isInsideIframe = window.self !== window.top;
      } catch (e) {
        isInsideIframe = true;
      }
      if (isEmbedParam || isInsideIframe) {
        setIsEmbedded(true);
        document.body.classList.add("embed-mode");
        document.documentElement.classList.add("embed-mode");
      }
    }
  }, [isEmbedParam]);
  
  // Interactive Widgets State
  const [roofArea, setRoofArea] = useState<number>(2000);
  const [wasteArea, setWasteArea] = useState<number>(1000);
  const [projectType, setProjectType] = useState<"tile" | "siding" | "flooring" | "drywall">("tile");
  const [layoutStyle, setLayoutStyle] = useState<"straight" | "diagonal">("straight");

  const handleCopy = (id: string, textToCopy: string) => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // Dead load calculations (weights in lbs per sqft)
  const weightAsphalt = Math.round(roofArea * 2.3);
  const weightMetal = Math.round(roofArea * 1.2);
  const weightTile = Math.round(roofArea * 9.5);
  const formatLbs = (val: number) => val.toLocaleString() + " lbs";

  // Waste margin calculations
  // Tile: Straight +8%, Diagonal +15%
  // Siding: Straight +5%, Diagonal +12%
  // Flooring: Straight +7%, Diagonal +15%
  // Drywall: Straight +10%, Diagonal +15%
  const getWasteMultiplier = () => {
    if (projectType === "tile") return layoutStyle === "straight" ? 0.08 : 0.15;
    if (projectType === "siding") return layoutStyle === "straight" ? 0.05 : 0.12;
    if (projectType === "flooring") return layoutStyle === "straight" ? 0.07 : 0.15;
    return layoutStyle === "straight" ? 0.10 : 0.15; // drywall
  };

  const wasteMultiplier = getWasteMultiplier();
  const rawWasteAmount = Math.round(wasteArea * wasteMultiplier);
  const totalRecommendedOrder = wasteArea + rawWasteAmount;

  // CONDITIONAL RENDER: Single Element Iframe Embeds
  if (embedMode === "regional") {
    return (
      <div className="w-full p-2 bg-transparent text-left">
        <section className="w-full flex flex-col gap-4">
          <div className="flex justify-between items-center border-b border-[var(--line-strong)] pb-2">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
              Meazora Regional Cost Variancy Index (2026)
            </h2>
          </div>
          <div className="border border-[var(--border)] rounded overflow-hidden">
            <table className="w-full text-left border-collapse text-[11px] font-sans">
              <thead>
                <tr className="bg-[var(--surface-muted)] border-b border-[var(--border)] font-mono text-[9px] text-[var(--ink)] uppercase">
                  <th className="p-2">U.S. Region</th>
                  <th className="p-2">Representative States</th>
                  <th className="p-2 text-right">Cost Multiplier</th>
                  <th className="p-2 text-right">Variance</th>
                </tr>
              </thead>
              <tbody>
                {REGIONAL_INDEX.map((row) => (
                  <tr key={row.region} className="border-b border-[var(--border)] hover:bg-[var(--surface-muted)]/30">
                    <td className="p-2 font-semibold text-[var(--ink)]">{row.region}</td>
                    <td className="p-2 text-[var(--ink-soft)] font-mono">{row.states}</td>
                    <td className="p-2 text-right font-bold text-[var(--accent)] font-mono">{row.multiplier}</td>
                    <td className="p-2 text-right text-[var(--ink-soft)] font-mono">{row.variance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-[var(--ink-soft)] text-center m-0">
            Source: <a href="https://www.meazora.com/statistics" target="_blank" className="text-[var(--accent)] font-bold">Meazora Statistics Hub</a>
          </p>
        </section>
      </div>
    );
  }

  if (embedMode === "inflation") {
    return (
      <div className="w-full p-2 bg-transparent text-left">
        <section className="w-full flex flex-col gap-4">
          <div className="flex justify-between items-center border-b border-[var(--line-strong)] pb-2">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
              Remodeling Cost Inflation Tracker (2024 vs. 2026)
            </h2>
          </div>
          <div className="border border-[var(--border)] rounded overflow-hidden">
            <table className="w-full text-left border-collapse text-[11px] font-sans">
              <thead>
                <tr className="bg-[var(--surface-muted)] border-b border-[var(--border)] font-mono text-[9px] text-[var(--ink)] uppercase">
                  <th className="p-2">Category</th>
                  <th className="p-2">Unit</th>
                  <th className="p-2 text-right">Avg Cost (2024)</th>
                  <th className="p-2 text-right">Avg Cost (2026)</th>
                  <th className="p-2 text-right">Increase</th>
                </tr>
              </thead>
              <tbody>
                {INFLATION_TRACKER.map((row) => (
                  <tr key={row.category} className="border-b border-[var(--border)] hover:bg-[var(--surface-muted)]/30">
                    <td className="p-2 font-semibold text-[var(--ink)]">{row.category}</td>
                    <td className="p-2 text-[var(--ink-soft)]">{row.unit}</td>
                    <td className="p-2 text-right text-[var(--ink-soft)] font-mono">{row.cost2024}</td>
                    <td className="p-2 text-right font-bold text-[var(--ink)] font-mono">{row.cost2026}</td>
                    <td className="p-2 text-right font-bold text-[var(--accent)] font-mono">{row.increase}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-[var(--ink-soft)] text-center m-0">
            Source: <a href="https://www.meazora.com/statistics" target="_blank" className="text-[var(--accent)] font-bold">Meazora Statistics Hub</a>
          </p>
        </section>
      </div>
    );
  }

  if (embedMode === "remediation") {
    return (
      <div className="w-full p-2 bg-transparent text-left">
        <section className="w-full flex flex-col gap-4">
          <div className="flex justify-between items-center border-b border-[var(--line-strong)] pb-2">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
              The Cost of DIY Failures (Remediation Matrix)
            </h2>
          </div>
          <div className="border border-[var(--border)] rounded overflow-hidden">
            <table className="w-full text-left border-collapse text-[11px] font-sans">
              <thead>
                <tr className="bg-[var(--surface-muted)] border-b border-[var(--border)] font-mono text-[9px] text-[var(--ink)] uppercase">
                  <th className="p-2">Project Type</th>
                  <th className="p-2">Critical Failure Point</th>
                  <th className="p-2 text-right">Initial Pro</th>
                  <th className="p-2 text-right">Remediation</th>
                  <th className="p-2 text-right">Penalty</th>
                </tr>
              </thead>
              <tbody>
                {DIY_FAILURES.map((row) => (
                  <tr key={row.project} className="border-b border-[var(--border)] hover:bg-[var(--surface-muted)]/30">
                    <td className="p-2 font-semibold text-[var(--ink)]">{row.project}</td>
                    <td className="p-2 text-[var(--ink-soft)]">{row.error}</td>
                    <td className="p-2 text-right text-[var(--ink-soft)] font-mono">{row.initialProCost}</td>
                    <td className="p-2 text-right font-bold text-rose-500 font-mono">{row.remediationCost}</td>
                    <td className="p-2 text-right font-bold text-[var(--accent)] font-mono">{row.penalty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-[var(--ink-soft)] text-center m-0">
            Source: <a href="https://www.meazora.com/statistics" target="_blank" className="text-[var(--accent)] font-bold">Meazora Statistics Hub</a>
          </p>
        </section>
      </div>
    );
  }

  if (embedMode === "deadload") {
    return (
      <div className="w-full p-2 bg-transparent text-left">
        <section className="w-full flex flex-col gap-4">
          <div className="flex justify-between items-center border-b border-[var(--line-strong)] pb-2">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
              Interactive Dead Load Structural Weight Calculator
            </h2>
          </div>
          <div className="p-4 bg-[var(--surface-muted)] border border-[var(--border)] rounded flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <label htmlFor="roofAreaInputEmbed" className="text-[10px] font-mono font-bold uppercase text-[var(--ink)]">
                Roof Area (Sq Ft):
              </label>
              <input
                id="roofAreaInputEmbed"
                type="number"
                value={roofArea}
                onChange={(e) => setRoofArea(Math.max(1, parseInt(e.target.value) || 0))}
                className="px-2 py-1 text-xs bg-[var(--surface)] text-[var(--ink)] border border-[var(--border)] rounded font-mono max-w-[120px] focus:outline-none focus:border-[var(--accent)]"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col gap-1">
                <span className="text-[9px] font-mono text-[var(--ink-faint)] uppercase">Standing Seam Metal</span>
                <span className="text-base font-bold text-[var(--ink)] font-mono">{formatLbs(weightMetal)}</span>
              </div>
              <div className="p-3 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col gap-1">
                <span className="text-[9px] font-mono text-[var(--ink-faint)] uppercase">Asphalt Shingles</span>
                <span className="text-base font-bold text-[var(--ink)] font-mono">{formatLbs(weightAsphalt)}</span>
              </div>
              <div className="p-3 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col gap-1 border-rose-500/20">
                <span className="text-[9px] font-mono text-rose-500 uppercase font-bold">Clay & Slate Tile</span>
                <span className="text-base font-bold text-rose-500 font-mono">{formatLbs(weightTile)}</span>
              </div>
            </div>
          </div>
          <p className="text-[10px] text-[var(--ink-soft)] text-center m-0">
            Source: <a href="https://www.meazora.com/statistics" target="_blank" className="text-[var(--accent)] font-bold">Meazora Statistics Hub</a>
          </p>
        </section>
      </div>
    );
  }

  if (embedMode === "spoilage") {
    return (
      <div className="w-full p-2 bg-transparent text-left">
        <section className="w-full flex flex-col gap-4">
          <div className="flex justify-between items-center border-b border-[var(--line-strong)] pb-2">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
              Interactive Material Waste & Spoilage Estimator
            </h2>
          </div>
          <div className="p-4 bg-[var(--surface-muted)] border border-[var(--border)] rounded flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="wasteAreaInputEmbed" className="text-[9px] font-mono font-bold uppercase text-[var(--ink)]">Net Sq Footage:</label>
                <input
                  id="wasteAreaInputEmbed"
                  type="number"
                  value={wasteArea}
                  onChange={(e) => setWasteArea(Math.max(1, parseInt(e.target.value) || 0))}
                  className="px-2 py-1 text-xs bg-[var(--surface)] text-[var(--ink)] border border-[var(--border)] rounded font-mono focus:outline-none focus:border-[var(--accent)]"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="projectSelectEmbed" className="text-[9px] font-mono font-bold uppercase text-[var(--ink)]">Project Type:</label>
                <select
                  id="projectSelectEmbed"
                  value={projectType}
                  onChange={(e) => setProjectType(e.target.value as any)}
                  className="px-2 py-1 text-xs bg-[var(--surface)] text-[var(--ink)] border border-[var(--border)] rounded font-mono focus:outline-none focus:border-[var(--accent)]"
                >
                  <option value="tile">Shower/Wall Tile</option>
                  <option value="siding">Siding Panels</option>
                  <option value="flooring">Hardwood Flooring</option>
                  <option value="drywall">Drywall Panels</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="layoutSelectEmbed" className="text-[9px] font-mono font-bold uppercase text-[var(--ink)]">Layout Pattern:</label>
                <select
                  id="layoutSelectEmbed"
                  value={layoutStyle}
                  onChange={(e) => setLayoutStyle(e.target.value as any)}
                  className="px-2 py-1 text-xs bg-[var(--surface)] text-[var(--ink)] border border-[var(--border)] rounded font-mono focus:outline-none focus:border-[var(--accent)]"
                >
                  <option value="straight">Straight Grid</option>
                  <option value="diagonal">Diagonal/Chevron</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-[var(--border)]">
              <div className="p-2 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col gap-0.5">
                <span className="text-[8px] font-mono text-[var(--ink-faint)] uppercase">Waste Factor</span>
                <span className="text-sm font-bold text-[var(--ink)] font-mono">{Math.round(wasteMultiplier * 100)}%</span>
              </div>
              <div className="p-2 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col gap-0.5">
                <span className="text-[8px] font-mono text-[var(--ink-faint)] uppercase">Cut Loss Allowance</span>
                <span className="text-sm font-bold text-[var(--ink)] font-mono">{rawWasteAmount.toLocaleString()} Sq Ft</span>
              </div>
              <div className="p-2 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col gap-0.5 border-[var(--accent)]/30">
                <span className="text-[8px] font-mono text-[var(--accent)] uppercase font-bold">Total to Purchase</span>
                <span className="text-sm font-bold text-[var(--accent)] font-mono">{totalRecommendedOrder.toLocaleString()} Sq Ft</span>
              </div>
            </div>
          </div>
          <p className="text-[10px] text-[var(--ink-soft)] text-center m-0">
            Source: <a href="https://www.meazora.com/statistics" target="_blank" className="text-[var(--accent)] font-bold">Meazora Statistics Hub</a>
          </p>
        </section>
      </div>
    );
  }
  if (embedMode === "energy") {
    return (
      <div className="w-full p-2 bg-transparent text-left">
        <section className="w-full flex flex-col gap-4">
          <div className="flex justify-between items-center border-b border-[var(--line-strong)] pb-2">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
              Energy Upgrade Cost Payback & ROI Timelines
            </h2>
          </div>
          <div className="border border-[var(--border)] rounded overflow-hidden">
            <table className="w-full text-left border-collapse text-[11px] font-sans">
              <thead>
                <tr className="bg-[var(--surface-muted)] border-b border-[var(--border)] font-mono text-[9px] text-[var(--ink)] uppercase">
                  <th className="p-2">Energy Upgrade</th>
                  <th className="p-2 text-right">Avg Upfront Cost</th>
                  <th className="p-2 text-right">Avg Monthly Savings</th>
                  <th className="p-2 text-right">Payback Period</th>
                </tr>
              </thead>
              <tbody>
                {ENERGY_PAYBACK.map((row) => (
                  <tr key={row.upgrade} className="border-b border-[var(--border)] hover:bg-[var(--surface-muted)]/30">
                    <td className="p-2 font-semibold text-[var(--ink)]">{row.upgrade}</td>
                    <td className="p-2 text-right text-[var(--ink-soft)] font-mono">{row.avgUpfrontCost}</td>
                    <td className="p-2 text-right font-bold text-[var(--ink)] font-mono">{row.energyBillSavings}</td>
                    <td className="p-2 text-right font-bold text-[var(--accent)] font-mono">{row.paybackPeriod}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-[var(--ink-soft)] text-center m-0">
            Source: <a href="https://www.meazora.com/statistics" target="_blank" className="text-[var(--accent)] font-bold">Meazora Statistics Hub</a>
          </p>
        </section>
      </div>
    );
  }

  if (embedMode === "roi" || embedMode === "roi-kitchen" || embedMode === "roi-siding" || embedMode === "roi-roofing") {
    const selectedRoi = embedMode === "roi" 
      ? STATS_DATA.roi 
      : STATS_DATA.roi.filter(item => item.id === embedMode);

    return (
      <div className="w-full p-2 bg-transparent text-left">
        <section className="w-full flex flex-col gap-4">
          <div className="flex justify-between items-center border-b border-[var(--line-strong)] pb-2">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
              {embedMode === "roi" ? "Remodeling Resale Return on Investment (ROI)" : "Remodeling Resale ROI Component"}
            </h2>
          </div>
          <div className="flex flex-col gap-4">
            {selectedRoi.map((stat) => (
              <div key={stat.id} className="p-3 bg-[var(--surface)] border border-[var(--border)] rounded flex justify-between items-center gap-4 card-blueprint">
                <div className="flex-1 flex flex-col gap-1">
                  <span className="text-base font-bold text-[var(--accent)] font-mono">{stat.dataPoint}</span>
                  <p className="text-xs text-[var(--ink-soft)] font-sans leading-normal m-0">{stat.claim}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[var(--ink-soft)] text-center m-0">
            Source: <a href="https://www.meazora.com/statistics" target="_blank" className="text-[var(--accent)] font-bold">Meazora Statistics Hub</a>
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center py-8 px-4 sm:px-6 md:px-12 w-full max-w-4xl mx-auto relative transition-colors duration-150 text-left">
      <header className="w-full flex flex-col items-start gap-4 mb-10 pb-6 border-b border-[var(--line)]">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded bg-[var(--surface-muted)] text-[var(--accent)] text-[9px] font-bold font-mono uppercase tracking-widest border border-[var(--border)]">
          Proprietary Research & Data Bank
        </span>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-wide text-[var(--ink)] leading-tight font-heading-condensed uppercase m-0">
          Home Improvement & Remodeling Statistics Index (2026)
        </h1>
        <p className="text-sm text-[var(--ink-soft)] leading-relaxed font-sans max-w-2xl m-0">
          A verified repository of construction economics, geographic multipliers, material safety specs, and remodeling return on investment (ROI) metrics. Certified for academic, editorial, and journalistic reference.
        </p>
        <div className="flex flex-wrap gap-4 mt-2">
          <a href="/data/remodeling-stats-2026.json" download className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--border)] rounded bg-[var(--surface)] text-[var(--ink-soft)] hover:text-[var(--accent)] hover:border-[var(--accent)] text-xs font-mono font-bold uppercase tracking-wider transition-all duration-150">
            💾 Download Raw JSON Dataset
          </a>
          <a href="#academic-citations" className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--border)] rounded bg-[var(--surface)] text-[var(--ink-soft)] hover:text-[var(--accent)] hover:border-[var(--accent)] text-xs font-mono font-bold uppercase tracking-wider transition-all duration-150">
            📚 Citation Guide (APA/MLA/Chicago)
          </a>
        </div>
      </header>

      {/* 1. Regional Cost Index Table */}
      <section className="w-full flex flex-col gap-6 mb-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[var(--line-strong)] pb-2 gap-4">
          <h2 className="text-xl font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
            1. Meazora Regional Cost Variancy Index (2026)
          </h2>
          <button
            onClick={() => handleCopy("index-cit", "Meazora. (2026). Regional Remodeling Cost Variancy Index. Construction Economic Database. URL: https://www.meazora.com/statistics/")}
            className={`px-3 py-1 font-mono text-[9px] uppercase tracking-wider font-bold border rounded transition-all cursor-pointer ${
              copiedId === "index-cit"
                ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
            }`}
          >
            {copiedId === "index-cit" ? "Copied!" : "Cite This Index"}
          </button>
        </div>
        <p className="text-xs text-[var(--ink-soft)] m-0">
          Geographic labor supply, weather requirements, and local building codes drive material variance. Use these multipliers against national averages to calculate regional estimates.
        </p>
        <div className="border border-[var(--border)] rounded overflow-hidden">
          <table className="w-full text-left border-collapse text-xs font-sans">
            <thead>
              <tr className="bg-[var(--surface-muted)] border-b border-[var(--border)] font-mono text-[10px] text-[var(--ink)] uppercase">
                <th className="p-3">U.S. Region</th>
                <th className="p-3">Representative States</th>
                <th className="p-3 text-right">Cost Multiplier</th>
                <th className="p-3 text-right">Variance</th>
              </tr>
            </thead>
            <tbody>
              {REGIONAL_INDEX.map((row) => (
                <tr key={row.region} className="border-b border-[var(--border)] hover:bg-[var(--surface-muted)]/30">
                  <td className="p-3 font-semibold text-[var(--ink)]">{row.region}</td>
                  <td className="p-3 text-[var(--ink-soft)] font-mono">{row.states}</td>
                  <td className="p-3 text-right font-bold text-[var(--accent)] font-mono">{row.multiplier}</td>
                  <td className="p-3 text-right text-[var(--ink-soft)] font-mono">{row.variance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Copyable Embed Code Panel */}
        <div className="mt-2">
          <button
            onClick={() => setShowEmbed1(!showEmbed1)}
            className="flex items-center justify-between py-1 text-left text-[10px] font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] transition-colors cursor-pointer"
          >
            <span>🔌 Embed This Regional Index Chart</span>
            <span className="ml-2">{showEmbed1 ? "▲" : "▼"}</span>
          </button>
          {showEmbed1 && (
            <div className="mt-2 p-3 rounded bg-[var(--surface-muted)] border border-[var(--border)] flex flex-col gap-2">
              <p className="text-[10px] text-[var(--ink-soft)] leading-normal m-0">
                Copy and paste the HTML code below to embed this chart into your blog or website:
              </p>
              <textarea
                readOnly
                className="w-full p-2 bg-[var(--surface)] text-[var(--ink-soft)] rounded border border-[var(--border)] h-16 resize-none font-mono text-[9px] focus:outline-none select-all"
                onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                value={`<iframe src="https://www.meazora.com/statistics?embed=regional" width="100%" height="350" frameborder="0"></iframe>\n<p style="font-size:11px;text-align:center;">Chart powered by the <a href="https://www.meazora.com/statistics">Meazora Statistics Hub</a></p>`}
              />
            </div>
          )}
        </div>
      </section>

      {/* 2. Construction Inflation Tracker */}
      <section className="w-full flex flex-col gap-6 mb-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[var(--line-strong)] pb-2 gap-4">
          <h2 className="text-xl font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
            2. Remodeling Cost Inflation Tracker (2024 vs. 2026)
          </h2>
          <button
            onClick={() => handleCopy("inflation-cit", "Meazora. (2026). Remodeling Inflation Index (2024 vs 2026). Material and Labor Pricing Database. URL: https://www.meazora.com/statistics/")}
            className={`px-3 py-1 font-mono text-[9px] uppercase tracking-wider font-bold border rounded transition-all cursor-pointer ${
              copiedId === "inflation-cit"
                ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
            }`}
          >
            {copiedId === "inflation-cit" ? "Copied!" : "Cite Inflation Tracker"}
          </button>
        </div>
        <p className="text-xs text-[var(--ink-soft)] m-0">
          Supply-chain friction and rising petroleum product base costs have driven sharp increases in siding and asphalt-based materials.
        </p>
        <div className="border border-[var(--border)] rounded overflow-hidden">
          <table className="w-full text-left border-collapse text-xs font-sans">
            <thead>
              <tr className="bg-[var(--surface-muted)] border-b border-[var(--border)] font-mono text-[10px] text-[var(--ink)] uppercase">
                <th className="p-3">Category</th>
                <th className="p-3">Unit</th>
                <th className="p-3 text-right">Avg Cost (2024)</th>
                <th className="p-3 text-right">Avg Cost (2026)</th>
                <th className="p-3 text-right">Increase</th>
              </tr>
            </thead>
            <tbody>
              {INFLATION_TRACKER.map((row) => (
                <tr key={row.category} className="border-b border-[var(--border)] hover:bg-[var(--surface-muted)]/30">
                  <td className="p-3 font-semibold text-[var(--ink)]">{row.category}</td>
                  <td className="p-3 text-[var(--ink-soft)]">{row.unit}</td>
                  <td className="p-3 text-right text-[var(--ink-soft)] font-mono">{row.cost2024}</td>
                  <td className="p-3 text-right font-bold text-[var(--ink)] font-mono">{row.cost2026}</td>
                  <td className="p-3 text-right font-bold text-[var(--accent)] font-mono">{row.increase}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Copyable Embed Code Panel */}
        <div className="mt-2">
          <button
            onClick={() => setShowEmbed2(!showEmbed2)}
            className="flex items-center justify-between py-1 text-left text-[10px] font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] transition-colors cursor-pointer"
          >
            <span>🔌 Embed This Inflation Tracker Chart</span>
            <span className="ml-2">{showEmbed2 ? "▲" : "▼"}</span>
          </button>
          {showEmbed2 && (
            <div className="mt-2 p-3 rounded bg-[var(--surface-muted)] border border-[var(--border)] flex flex-col gap-2">
              <p className="text-[10px] text-[var(--ink-soft)] leading-normal m-0">
                Copy and paste the HTML code below to embed this chart into your blog or website:
              </p>
              <textarea
                readOnly
                className="w-full p-2 bg-[var(--surface)] text-[var(--ink-soft)] rounded border border-[var(--border)] h-16 resize-none font-mono text-[9px] focus:outline-none select-all"
                onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                value={`<iframe src="https://www.meazora.com/statistics?embed=inflation" width="100%" height="320" frameborder="0"></iframe>\n<p style="font-size:11px;text-align:center;">Chart powered by the <a href="https://www.meazora.com/statistics">Meazora Statistics Hub</a></p>`}
              />
            </div>
          )}
        </div>
      </section>

      {/* 3. DIY Failure Remediation Cost Matrix */}
      <section className="w-full flex flex-col gap-6 mb-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[var(--line-strong)] pb-2 gap-4">
          <h2 className="text-xl font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
            3. The Cost of DIY Failures (Remediation Matrix)
          </h2>
          <button
            onClick={() => handleCopy("failures-cit", "Meazora. (2026). DIY Remediation Cost Index. Residential Construction Safety Report. URL: https://www.meazora.com/statistics/")}
            className={`px-3 py-1 font-mono text-[9px] uppercase tracking-wider font-bold border rounded transition-all cursor-pointer ${
              copiedId === "failures-cit"
                ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
            }`}
          >
            {copiedId === "failures-cit" ? "Copied!" : "Cite Remediation Data"}
          </button>
        </div>
        <p className="text-xs text-[var(--ink-soft)] m-0">
          DIY errors in structural framing or waterproofing lead to code violations and internal rot. Remediating a failed DIY assembly regularly costs over 2x hiring a licensed professional initially.
        </p>
        <div className="border border-[var(--border)] rounded overflow-hidden">
          <table className="w-full text-left border-collapse text-xs font-sans">
            <thead>
              <tr className="bg-[var(--surface-muted)] border-b border-[var(--border)] font-mono text-[10px] text-[var(--ink)] uppercase">
                <th className="p-3">Project Type</th>
                <th className="p-3">Critical DIY Failure Point</th>
                <th className="p-3 text-right">Initial Pro Cost</th>
                <th className="p-3 text-right">Pro Remediation Cost</th>
                <th className="p-3 text-right">Cost Penalty</th>
              </tr>
            </thead>
            <tbody>
              {DIY_FAILURES.map((row) => (
                <tr key={row.project} className="border-b border-[var(--border)] hover:bg-[var(--surface-muted)]/30">
                  <td className="p-3 font-semibold text-[var(--ink)]">{row.project}</td>
                  <td className="p-3 text-[var(--ink-soft)]">{row.error}</td>
                  <td className="p-3 text-right text-[var(--ink-soft)] font-mono">{row.initialProCost}</td>
                  <td className="p-3 text-right font-bold text-rose-500 font-mono">{row.remediationCost}</td>
                  <td className="p-3 text-right font-bold text-[var(--accent)] font-mono">{row.penalty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Copyable Embed Code Panel */}
        <div className="mt-2">
          <button
            onClick={() => setShowEmbed3(!showEmbed3)}
            className="flex items-center justify-between py-1 text-left text-[10px] font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] transition-colors cursor-pointer"
          >
            <span>🔌 Embed This DIY Remediation Chart</span>
            <span className="ml-2">{showEmbed3 ? "▲" : "▼"}</span>
          </button>
          {showEmbed3 && (
            <div className="mt-2 p-3 rounded bg-[var(--surface-muted)] border border-[var(--border)] flex flex-col gap-2">
              <p className="text-[10px] text-[var(--ink-soft)] leading-normal m-0">
                Copy and paste the HTML code below to embed this chart into your blog or website:
              </p>
              <textarea
                readOnly
                className="w-full p-2 bg-[var(--surface)] text-[var(--ink-soft)] rounded border border-[var(--border)] h-16 resize-none font-mono text-[9px] focus:outline-none select-all"
                onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                value={`<iframe src="https://www.meazora.com/statistics?embed=remediation" width="100%" height="320" frameborder="0"></iframe>\n<p style="font-size:11px;text-align:center;">Chart powered by the <a href="https://www.meazora.com/statistics">Meazora Statistics Hub</a></p>`}
              />
            </div>
          )}
        </div>
      </section>

      {/* 4. Home Energy Upgrade Payback Timelines */}
      <section className="w-full flex flex-col gap-6 mb-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[var(--line-strong)] pb-2 gap-4">
          <h2 className="text-xl font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
            4. Energy Upgrade Cost Payback & ROI Timelines
          </h2>
          <button
            onClick={() => handleCopy("energy-cit", "Meazora. (2026). Residential Insulation and HVAC Payback Analysis. Energy Cost Index. URL: https://www.meazora.com/statistics/")}
            className={`px-3 py-1 font-mono text-[9px] uppercase tracking-wider font-bold border rounded transition-all cursor-pointer ${
              copiedId === "energy-cit"
                ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
            }`}
          >
            {copiedId === "energy-cit" ? "Copied!" : "Cite Energy Payback"}
          </button>
        </div>
        <p className="text-xs text-[var(--ink-soft)] m-0">
          Payback timelines reflect upfront insulation/retrofit budgets compared to monthly utility savings based on climate zone averages.
        </p>
        <div className="border border-[var(--border)] rounded overflow-hidden">
          <table className="w-full text-left border-collapse text-xs font-sans">
            <thead>
              <tr className="bg-[var(--surface-muted)] border-b border-[var(--border)] font-mono text-[10px] text-[var(--ink)] uppercase">
                <th className="p-3">Energy Upgrade</th>
                <th className="p-3 text-right">Avg Upfront Cost</th>
                <th className="p-3 text-right">Avg Monthly Savings</th>
                <th className="p-3 text-right">Payback Period</th>
              </tr>
            </thead>
            <tbody>
              {ENERGY_PAYBACK.map((row) => (
                <tr key={row.upgrade} className="border-b border-[var(--border)] hover:bg-[var(--surface-muted)]/30">
                  <td className="p-3 font-semibold text-[var(--ink)]">{row.upgrade}</td>
                  <td className="p-3 text-right text-[var(--ink-soft)] font-mono">{row.avgUpfrontCost}</td>
                  <td className="p-3 text-right font-bold text-[var(--ink)] font-mono">{row.energyBillSavings}</td>
                  <td className="p-3 text-right font-bold text-[var(--accent)] font-mono">{row.paybackPeriod}</td>
                </tr>
              ))}
          </tbody>
          </table>
        </div>

        {/* Copyable Embed Code Panel */}
        <div className="mt-2">
          <button
            onClick={() => setShowEmbed6(!showEmbed6)}
            className="flex items-center justify-between py-1 text-left text-[10px] font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] transition-colors cursor-pointer"
          >
            <span>🔌 Embed This Energy Payback Chart</span>
            <span className="ml-2">{showEmbed6 ? "▲" : "▼"}</span>
          </button>
          {showEmbed6 && (
            <div className="mt-2 p-3 rounded bg-[var(--surface-muted)] border border-[var(--border)] flex flex-col gap-2">
              <p className="text-[10px] text-[var(--ink-soft)] leading-normal m-0">
                Copy and paste the HTML code below to embed this chart into your blog or website:
              </p>
              <textarea
                readOnly
                className="w-full p-2 bg-[var(--surface)] text-[var(--ink-soft)] rounded border border-[var(--border)] h-16 resize-none font-mono text-[9px] focus:outline-none select-all"
                onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                value={`<iframe src="https://www.meazora.com/statistics?embed=energy" width="100%" height="320" frameborder="0"></iframe>\n<p style="font-size:11px;text-align:center;">Chart powered by the <a href="https://www.meazora.com/statistics">Meazora Statistics Hub</a></p>`}
              />
            </div>
          )}
        </div>
      </section>

      {/* 5. Dead Load Calculator Widget */}
      <section className="w-full flex flex-col gap-6 mb-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[var(--line-strong)] pb-2 gap-4">
          <h2 className="text-xl font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
            5. Interactive Dead Load Structural Weight Calculator
          </h2>
          <button
            onClick={() => handleCopy("load-cit", `Meazora. (2026). Roofing Material Dead Load Calculations (Size: ${roofArea} sq ft). URL: https://www.meazora.com/statistics/`)}
            className={`px-3 py-1 font-mono text-[9px] uppercase tracking-wider font-bold border rounded transition-all cursor-pointer ${
              copiedId === "load-cit"
                ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
            }`}
          >
            {copiedId === "load-cit" ? "Copied!" : "Cite Current Load Values"}
          </button>
        </div>
        <p className="text-xs text-[var(--ink-soft)] m-0">
          Calculate the exact dead load weight added to residential structural framing depending on roof square footage and material selection.
        </p>
        <div className="p-6 bg-[var(--surface-muted)] border border-[var(--border)] rounded flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <label htmlFor="roofAreaInput" className="text-xs font-mono font-bold uppercase text-[var(--ink)]">
              Enter Roof Square Footage:
            </label>
            <input
              id="roofAreaInput"
              type="number"
              value={roofArea}
              onChange={(e) => setRoofArea(Math.max(1, parseInt(e.target.value) || 0))}
              className="px-3 py-1.5 text-sm bg-[var(--surface)] text-[var(--ink)] border border-[var(--border)] rounded font-mono max-w-[150px] focus:outline-none focus:border-[var(--accent)]"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col gap-1">
              <span className="text-[10px] font-mono text-[var(--ink-faint)] uppercase">Standing Seam Metal</span>
              <span className="text-lg font-bold text-[var(--ink)] font-mono">{formatLbs(weightMetal)}</span>
              <span className="text-[10px] text-[var(--ink-soft)]">Lightest roofing option.</span>
            </div>
            <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col gap-1">
              <span className="text-[10px] font-mono text-[var(--ink-faint)] uppercase">Asphalt Shingles</span>
              <span className="text-lg font-bold text-[var(--ink)] font-mono">{formatLbs(weightAsphalt)}</span>
              <span className="text-[10px] text-[var(--ink-soft)]">~2x heavier than metal.</span>
            </div>
            <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col gap-1 border-rose-500/20">
              <span className="text-[10px] font-mono text-rose-500 uppercase font-bold">Clay & Slate Tile</span>
              <span className="text-lg font-bold text-rose-500 font-mono">{formatLbs(weightTile)}</span>
              <span className="text-[10px] text-[var(--ink-soft)]">Structural evaluation required.</span>
            </div>
          </div>
        </div>

        {/* Copyable Embed Code Panel */}
        <div className="mt-2">
          <button
            onClick={() => setShowEmbed4(!showEmbed4)}
            className="flex items-center justify-between py-1 text-left text-[10px] font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] transition-colors cursor-pointer"
          >
            <span>🔌 Embed This Structural Weight Calculator</span>
            <span className="ml-2">{showEmbed4 ? "▲" : "▼"}</span>
          </button>
          {showEmbed4 && (
            <div className="mt-2 p-3 rounded bg-[var(--surface-muted)] border border-[var(--border)] flex flex-col gap-2">
              <p className="text-[10px] text-[var(--ink-soft)] leading-normal m-0">
                Copy and paste the HTML code below to embed this tool into your blog or website:
              </p>
              <textarea
                readOnly
                className="w-full p-2 bg-[var(--surface)] text-[var(--ink-soft)] rounded border border-[var(--border)] h-16 resize-none font-mono text-[9px] focus:outline-none select-all"
                onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                value={`<iframe src="https://www.meazora.com/statistics?embed=deadload" width="100%" height="240" frameborder="0"></iframe>\n<p style="font-size:11px;text-align:center;">Calculator powered by the <a href="https://www.meazora.com/statistics">Meazora Statistics Hub</a></p>`}
              />
            </div>
          )}
        </div>
      </section>

      {/* 6. Material Waste & Spoilage Estimator */}
      <section className="w-full flex flex-col gap-6 mb-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[var(--line-strong)] pb-2 gap-4">
          <h2 className="text-xl font-extrabold uppercase tracking-wider text-[var(--ink)] m-0">
            6. Interactive Material Waste & Spoilage Estimator
          </h2>
          <button
            onClick={() => handleCopy("waste-cit", `Meazora. (2026). Recommended Material Spoilage Factor (Project: ${projectType}, Style: ${layoutStyle}). URL: https://www.meazora.com/statistics/`)}
            className={`px-3 py-1 font-mono text-[9px] uppercase tracking-wider font-bold border rounded transition-all cursor-pointer ${
              copiedId === "waste-cit"
                ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
            }`}
          >
            {copiedId === "waste-cit" ? "Copied!" : "Cite Waste Multipliers"}
          </button>
        </div>
        <p className="text-xs text-[var(--ink-soft)] m-0">
          Compute the ideal safety buffer required when ordering finishes. Diagonal layouts (Herringbone, Chevron) require significantly higher margins to account for angled cuts.
        </p>
        <div className="p-6 bg-[var(--surface-muted)] border border-[var(--border)] rounded flex flex-col gap-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="wasteAreaInput" className="text-[10px] font-mono font-bold uppercase text-[var(--ink)]">Net Square Footage:</label>
              <input
                id="wasteAreaInput"
                type="number"
                value={wasteArea}
                onChange={(e) => setWasteArea(Math.max(1, parseInt(e.target.value) || 0))}
                className="px-3 py-1.5 text-sm bg-[var(--surface)] text-[var(--ink)] border border-[var(--border)] rounded font-mono focus:outline-none focus:border-[var(--accent)]"
              />
            </div>
            
            <div className="flex flex-col gap-2">
              <label htmlFor="projectSelect" className="text-[10px] font-mono font-bold uppercase text-[var(--ink)]">Project Type:</label>
              <select
                id="projectSelect"
                value={projectType}
                onChange={(e) => setProjectType(e.target.value as any)}
                className="px-3 py-1.5 text-sm bg-[var(--surface)] text-[var(--ink)] border border-[var(--border)] rounded font-sans focus:outline-none focus:border-[var(--accent)]"
              >
                <option value="tile">Tile Backsplash/Shower</option>
                <option value="siding">Siding Cladding</option>
                <option value="flooring">Hardwood/LVP Flooring</option>
                <option value="drywall">Drywall Panels</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="layoutSelect" className="text-[10px] font-mono font-bold uppercase text-[var(--ink)]">Layout complexity:</label>
              <select
                id="layoutSelect"
                value={layoutStyle}
                onChange={(e) => setLayoutStyle(e.target.value as any)}
                className="px-3 py-1.5 text-sm bg-[var(--surface)] text-[var(--ink)] border border-[var(--border)] rounded font-sans focus:outline-none focus:border-[var(--accent)]"
              >
                <option value="straight">Straight Layout (Standard)</option>
                <option value="diagonal">Diagonal / Herringbone / Chevron</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-[var(--border)] pt-4">
            <div className="flex flex-col">
              <span className="text-[10px] font-mono text-[var(--ink-faint)] uppercase">Recommended Margin</span>
              <span className="text-lg font-bold text-[var(--accent)] font-mono">+{(wasteMultiplier * 100).toFixed(0)}%</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-mono text-[var(--ink-faint)] uppercase">Waste Square Footage</span>
              <span className="text-lg font-bold text-[var(--ink)] font-mono">{rawWasteAmount.toLocaleString()} Sq Ft</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-mono text-[var(--ink-faint)] uppercase">Total Recommended Order</span>
              <span className="text-lg font-bold text-[var(--ink)] font-mono">{totalRecommendedOrder.toLocaleString()} Sq Ft</span>
            </div>
          </div>
        </div>

        {/* Copyable Embed Code Panel */}
        <div className="mt-2">
          <button
            onClick={() => setShowEmbed5(!showEmbed5)}
            className="flex items-center justify-between py-1 text-left text-[10px] font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] transition-colors cursor-pointer"
          >
            <span>🔌 Embed This Spoilage Estimator</span>
            <span className="ml-2">{showEmbed5 ? "▲" : "▼"}</span>
          </button>
          {showEmbed5 && (
            <div className="mt-2 p-3 rounded bg-[var(--surface-muted)] border border-[var(--border)] flex flex-col gap-2">
              <p className="text-[10px] text-[var(--ink-soft)] leading-normal m-0">
                Copy and paste the HTML code below to embed this tool into your blog or website:
              </p>
              <textarea
                readOnly
                className="w-full p-2 bg-[var(--surface)] text-[var(--ink-soft)] rounded border border-[var(--border)] h-16 resize-none font-mono text-[9px] focus:outline-none select-all"
                onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                value={`<iframe src="https://www.meazora.com/statistics?embed=spoilage" width="100%" height="240" frameborder="0"></iframe>\n<p style="font-size:11px;text-align:center;">Calculator powered by the <a href="https://www.meazora.com/statistics">Meazora Statistics Hub</a></p>`}
              />
            </div>
          )}
        </div>
      </section>

      {/* 7. Remodeling Return on Investment (ROI) */}
      <section className="w-full flex flex-col gap-6">
        <h2 className="text-xl font-extrabold uppercase tracking-wider text-[var(--ink)] border-b border-[var(--line-strong)] pb-2 m-0">
          7. Remodeling Resale Return on Investment (ROI)
        </h2>
        <div className="flex flex-col gap-6">
          {STATS_DATA.roi.map((stat) => {
            const isEmbedOpen = activeRoiEmbedId === stat.id;
            return (
              <div key={stat.id} className="p-5 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col gap-4 card-blueprint">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex-1 flex flex-col gap-2">
                    <span className="text-lg font-bold text-[var(--accent)] font-mono">{stat.dataPoint}</span>
                    <p className="text-sm text-[var(--ink-soft)] font-sans leading-relaxed m-0">{stat.claim}</p>
                    <span className="text-[10px] font-mono text-[var(--ink-faint)] uppercase">Verified Source: {stat.sourceName}</span>
                  </div>
                  <div className="flex flex-row md:flex-col gap-2 shrink-0 font-mono text-[9px] uppercase tracking-wider font-bold">
                    <button
                      onClick={() => handleCopy(`${stat.id}-html`, stat.htmlEmbed)}
                      className={`px-3 py-2 border rounded transition-all cursor-pointer ${
                        copiedId === `${stat.id}-html`
                          ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                          : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
                      }`}
                    >
                      {copiedId === `${stat.id}-html` ? "Copied!" : "Copy HTML Backlink"}
                    </button>
                    <button
                      onClick={() => handleCopy(`${stat.id}-mla`, stat.citationText)}
                      className={`px-3 py-2 border rounded transition-all cursor-pointer ${
                        copiedId === `${stat.id}-mla`
                          ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                          : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
                      }`}
                    >
                      {copiedId === `${stat.id}-mla` ? "Copied!" : "Copy MLA Citation"}
                    </button>
                  </div>
                </div>

                {/* Individual Embed Code Panel */}
                <div className="border-t border-[var(--border)] pt-3 mt-1">
                  <button
                    onClick={() => setActiveRoiEmbedId(isEmbedOpen ? null : stat.id)}
                    className="flex items-center justify-between py-1 text-left text-[10px] font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] transition-colors cursor-pointer"
                  >
                    <span>🔌 Embed Only This ROI Card</span>
                    <span className="ml-2">{isEmbedOpen ? "▲" : "▼"}</span>
                  </button>
                  {isEmbedOpen && (
                    <div className="mt-2 p-3 rounded bg-[var(--surface-muted)] border border-[var(--border)] flex flex-col gap-2">
                      <p className="text-[10px] text-[var(--ink-soft)] leading-normal m-0">
                        Copy and paste the HTML code below to embed this specific card into your blog:
                      </p>
                      <textarea
                        readOnly
                        className="w-full p-2 bg-[var(--surface)] text-[var(--ink-soft)] rounded border border-[var(--border)] h-16 resize-none font-mono text-[9px] focus:outline-none select-all"
                        onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                        value={`<iframe src="https://www.meazora.com/statistics?embed=${stat.id}" width="100%" height="160" frameborder="0"></iframe>\n<p style="font-size:11px;text-align:center;">Data provided by the <a href="https://www.meazora.com/statistics">Meazora Statistics Hub</a></p>`}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 8. Collapsible Citation Guide Accordion */}
      <section id="academic-citations" className="w-full flex flex-col gap-6 mt-12 pt-8 border-t border-[var(--line)]">
        <h2 className="text-xl font-extrabold uppercase tracking-wider text-[var(--ink)] border-b border-[var(--line-strong)] pb-2 m-0">
          8. Academic & Press Citation Index (MLA / APA / Chicago)
        </h2>
        <p className="text-xs text-[var(--ink-soft)] m-0">
          Copy the pre-formatted citations below to credit Meazora in academic, technical, or journalistic publications.
        </p>
        <div className="flex flex-col gap-4">
          <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col sm:flex-row sm:items-center justify-between gap-4 card-blueprint">
            <div className="flex-1 flex flex-col gap-1">
              <span className="text-[10px] font-mono text-[var(--accent)] uppercase font-bold">APA Format</span>
              <p className="text-xs text-[var(--ink-soft)] font-serif italic m-0">
                Meazora. (2026). Remodeling & Home Improvement Statistics Database. Retrieved from https://www.meazora.com/statistics/
              </p>
            </div>
            <button
              onClick={() => handleCopy("cit-apa", "Meazora. (2026). Remodeling & Home Improvement Statistics Database. Retrieved from https://www.meazora.com/statistics/")}
              className={`px-3 py-2 font-mono text-[9px] uppercase tracking-wider font-bold border rounded shrink-0 transition-all cursor-pointer ${
                copiedId === "cit-apa"
                  ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                  : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)]"
              }`}
            >
              {copiedId === "cit-apa" ? "Copied!" : "Copy APA"}
            </button>
          </div>

          <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col sm:flex-row sm:items-center justify-between gap-4 card-blueprint">
            <div className="flex-1 flex flex-col gap-1">
              <span className="text-[10px] font-mono text-[var(--accent)] uppercase font-bold">MLA Format</span>
              <p className="text-xs text-[var(--ink-soft)] font-serif italic m-0">
                Meazora. "Remodeling & Home Improvement Statistics Database." 2026, https://www.meazora.com/statistics/.
              </p>
            </div>
            <button
              onClick={() => handleCopy("cit-mla", 'Meazora. "Remodeling & Home Improvement Statistics Database." 2026, https://www.meazora.com/statistics/.')}
              className={`px-3 py-2 font-mono text-[9px] uppercase tracking-wider font-bold border rounded shrink-0 transition-all cursor-pointer ${
                copiedId === "cit-mla"
                  ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                  : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)]"
              }`}
            >
              {copiedId === "cit-mla" ? "Copied!" : "Copy MLA"}
            </button>
          </div>

          <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col sm:flex-row sm:items-center justify-between gap-4 card-blueprint">
            <div className="flex-1 flex flex-col gap-1">
              <span className="text-[10px] font-mono text-[var(--accent)] uppercase font-bold">Chicago Format</span>
              <p className="text-xs text-[var(--ink-soft)] font-serif italic m-0">
                Meazora. "Remodeling & Home Improvement Statistics Database." Last modified 2026. https://www.meazora.com/statistics/.
              </p>
            </div>
            <button
              onClick={() => handleCopy("cit-chi", 'Meazora. "Remodeling & Home Improvement Statistics Database." Last modified 2026. https://www.meazora.com/statistics/.')}
              className={`px-3 py-2 font-mono text-[9px] uppercase tracking-wider font-bold border rounded shrink-0 transition-all cursor-pointer ${
                copiedId === "cit-chi"
                  ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                  : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)]"
              }`}
            >
              {copiedId === "cit-chi" ? "Copied!" : "Copy Chicago"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function StatisticsClient() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[var(--background)] animate-pulse" />}>
      <StatisticsClientContent />
    </Suspense>
  );
}
